export * from './common.types';
export * from './envio.types';
export * from './item.types';
export * from './lote.types';
export * from './status.types';

type CaseStatus =
  | 'initial_review'
  | 'documentation'
  | 'negotiation'
  | 'agreement_draft'
  | 'finalization'
  | 'closed'
  | 'forwarded';

export type CaseStatusLabel =
  | 'em-analise'
  | 'em-negociacao'
  | 'acordo-pendente'
  | 'concluido'
  | 'arquivado'
  | 'encaminhado';

export type Temperature = 'cold' | 'warm' | 'hot' | 'critical';

type TemperatureTrend = 'rising' | 'falling' | 'stable';

type NegotiationStatus =
  | 'awaiting_response'
  | 'proposal_received'
  | 'counter_proposal_sent'
  | 'analyzing_proposal'
  | 'ready_to_offer'
  | 'in_discussion'
  | 'pending_approval'
  | 'agreement_close';

type CaseAction =
  | 'offer_discount'
  | 'analyze_proposal'
  | 'send_counter_proposal'
  | 'request_documents'
  | 'schedule_meeting'
  | 'approve_agreement';

export interface Case {
  id: string;
  caseNumber: string;
  company: string;
  team: string;
  status: CaseStatus;
  temperaturePercentage: number;
  temperatureTrend: TemperatureTrend;
  title: string;
  description: string;
  createdAt: Date;
  lastUpdate: Date;
  daysOpen: number;
  claimants: string[];
  respondents: string[];
  value: number;
  negotiationStatus?: NegotiationStatus;
  availableActions?: CaseAction[];
}

export interface DashboardMetrics {
  totalActiveCases: number;
  totalValue: number;
  averageTemperaturePercentage: number;
  successRate: number;
}

type MessageType = 'message' | 'proposal' | 'counter_proposal' | 'ai_suggestion' | 'ai_analysis';
type MessageSender = 'user' | 'counterparty' | 'ai';
type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'counter_proposed' | 'expired';

interface NegotiationMessage {
  id: string;
  caseId: string;
  type: MessageType;
  sender: MessageSender;
  senderName: string;
  content: string;
  timestamp: Date;
  read: boolean;
  proposalValue?: number;
  proposalConditions?: string;
  proposalStatus?: ProposalStatus;
  proposalDeadline?: Date;
  temperatureImpact?: number;
  aiConfidence?: number;
}

export interface NegotiationData {
  caseId: string;
  messages: NegotiationMessage[];
  currentTemperature: number;
  lastProposal?: NegotiationMessage;
  aiRecommendation?: string;
}

type MediationStatus = 'suggested' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
type MediationResult = 'with_agreement' | 'without_agreement' | 'pending';

export interface Mediation {
  id: string;
  caseId: string;
  caseNumber: string;
  caseTitle: string;
  status: MediationStatus;
  result: MediationResult;
  scheduledDate: Date;
  scheduledTime: string;
  mediatorName: string;
  mediatorEmail?: string;
  location?: string;
  isOnline: boolean;
  meetingLink?: string;
  claimants: string[];
  respondents: string[];
  observations?: string;
  aiSuggested: boolean;
  aiReason?: string;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
  agreementSummary?: string;
  temperatureBefore?: number;
  temperatureAfter?: number;
}

type DocumentCategory = 'contrato' | 'prova' | 'comunicacao';

export interface Document {
  id: string;
  caseId: string;
  fileName: string;
  fileSize: number;
  category: DocumentCategory;
  description?: string;
  uploadDate: Date;
  uploadedBy: string;
  version: number;
  url: string;
  mimeType: string;
  previousVersionId?: string;
}

type NotificationStatus = 'criada' | 'enviada' | 'entregue' | 'com_negociacao' | 'encerrada';
type NotificationChannel =
  | 'Email'
  | 'WhatsApp'
  | 'SMS'
  | 'Carta Registrada'
  | 'Carta Simples'
  | 'Aplicativo';

interface ChannelReadStatus {
  channel: NotificationChannel;
  sent: boolean;
  delivered: boolean;
  read: boolean;
  readAt?: Date;
  deliveredAt?: Date;
}

export interface Notification {
  id: string;
  notificationNumber: string;
  status: NotificationStatus;
  claimants: string[];
  respondents: string[];
  subject: string;
  description: string;
  channels: NotificationChannel[];
  channelStatuses?: ChannelReadStatus[];
  read: boolean;
  readAt?: Date;
  createdAt: Date;
  sentAt?: Date;
  deliveredAt?: Date;
  value?: number;
  hasNegotiation: boolean;
  caseId?: string;
  temperaturePercentage?: number;
}
